// frontend/src/components/LibraryCatalog.jsx
// Library catalog component for browsing music collection

import React, { useState, useEffect } from 'react';

const LibraryCatalog = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');

  // Debug logging
  console.log('🎵 LibraryCatalog component mounted');

  useEffect(() => {
    console.log('🔄 LibraryCatalog useEffect triggered');
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      setLoading(true);
      
      // Option 1: Try API Gateway endpoint first
      console.log('🎵 Trying API Gateway endpoint...');
      const apiResponse = await fetch('https://5gp7mnnjwf.execute-api.us-west-2.amazonaws.com/prod/catalog/librosa');
      if (apiResponse.ok) {
        const data = await apiResponse.json();
        setTracks(data.tracks || []);
        console.log('✅ API Gateway successful:', data.tracks?.length, 'tracks');
        return;
      }
      
      // Option 2: Try direct S3 access to aggregated file
      console.log('🗂️ Trying direct S3 aggregated file...');
      const s3Response = await fetch('https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/metadata/librosa-analysis/aggregated-tracks.json');
      if (s3Response.ok) {
        const s3Data = await s3Response.json();
        setTracks(s3Data.tracks || s3Data || []);
        console.log('✅ S3 aggregated file successful:', s3Data.tracks?.length || s3Data?.length, 'tracks');
        return;
      }
      
      // Option 3: Try batch analysis summary
      console.log('📊 Trying batch analysis summary...');
      const batchResponse = await fetch('https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/metadata/batch-analysis-summary-2025-08-19.json');
      if (batchResponse.ok) {
        const batchData = await batchResponse.json();
        if (batchData.tracks) {
          setTracks(batchData.tracks);
          console.log('✅ Batch summary successful:', batchData.tracks.length, 'tracks');
          return;
        }
      }
      
      // Fallback to static data
      console.warn('⚠️ All sources failed, using fallback data');
      setTracks(getFallbackTracks());
      
    } catch (error) {
      console.error('❌ Error fetching tracks:', error);
      setTracks(getFallbackTracks());
    } finally {
      setLoading(false);
    }
  };

  const getFallbackTracks = () => [
    {
      id: 'track-1',
      track_title: 'Sample Track 1',
      artist: 'Rue de Vivre',
      genre: 'Electronic',
      bpm: 120,
      duration: '3:45'
    },
    {
      id: 'track-2', 
      track_title: 'Sample Track 2',
      artist: 'Rue de Vivre',
      genre: 'Hip Hop',
      bpm: 135,
      duration: '4:12'
    }
  ];

  const filteredTracks = tracks.filter(track => {
    const matchesSearch = !searchFilter || 
      track.track_title?.toLowerCase().includes(searchFilter.toLowerCase()) ||
      track.artist?.toLowerCase().includes(searchFilter.toLowerCase());
    
    const matchesGenre = selectedGenre === 'all' || track.genre === selectedGenre;
    
    return matchesSearch && matchesGenre;
  });

  if (loading) {
    console.log('📦 LibraryCatalog: Rendering loading state');
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading Library...</h2>
        <p>Fetching your music collection...</p>
        <div style={{ marginTop: '1rem', color: '#666' }}>
          Component loaded successfully - checking data sources...
        </div>
      </div>
    );
  }

  console.log('🎵 LibraryCatalog: Rendering with', tracks.length, 'tracks');

  return (
    <div style={{ padding: '2rem' }}>
      <header style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', color: '#333', marginBottom: '0.5rem' }}>
          Music Library
        </h1>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Browse your complete music collection
        </p>
      </header>

      <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search tracks or artists..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          style={{
            padding: '0.5rem',
            borderRadius: '4px',
            border: '1px solid #ddd',
            minWidth: '300px'
          }}
        />
        
        <select
          value={selectedGenre}
          onChange={(e) => setSelectedGenre(e.target.value)}
          style={{
            padding: '0.5rem',
            borderRadius: '4px',
            border: '1px solid #ddd'
          }}
        >
          <option value="all">All Genres</option>
          <option value="Electronic">Electronic</option>
          <option value="Hip Hop">Hip Hop</option>
          <option value="Pop">Pop</option>
          <option value="Rock">Rock</option>
        </select>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
      }}>
        {filteredTracks.map((track, index) => (
          <div
            key={track.id || index}
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 1fr 120px 80px 80px 200px',
              gap: '1rem',
              alignItems: 'center',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white'
            }}
          >
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              {index + 1}
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#333' }}>
                {track.track_title || 'Untitled'}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {track.artist || 'Unknown'}
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {track.genre || 'Unknown'}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {track.bpm || '--'} BPM
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {track.duration || '--'}
            </div>
            <audio 
              controls 
              style={{ width: '100%', height: '32px' }}
            >
              {track.preview_url && <source src={track.preview_url} type="audio/mpeg" />}
              {track.s3_url && <source src={track.s3_url} type="audio/mpeg" />}
              <source src={`https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/playlists/${track.playlist || 'disco-pop---upbeat'}/audio/${track.filename || track.id}.mp3`} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        ))}
      </div>

      {filteredTracks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          <p>No tracks found matching your search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default LibraryCatalog;
